import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { BrowserProvider, Wallet } from "ethers";
import { useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitProvider, useDisconnect } from "@reown/appkit/react";
import { CHAIN_ID, robinhoodChain } from "./appkit";

const WalletCtx = createContext(null);

export const buildMessage = (action, dealId, ts) =>
  `Cross OTC Terminal\nAction: ${action}\nDeal: ${dealId}\nChain: ${CHAIN_ID}\nTimestamp: ${ts}`;

export function WalletProvider({ children }) {
  // Real wallets (MetaMask / Rabby / WalletConnect) are driven by Reown AppKit.
  const { open } = useAppKit();
  const { address: wcAddress, isConnected: wcConnected } = useAppKitAccount();
  const { chainId: wcChainId, switchNetwork } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider("eip155");
  const { disconnect: wcDisconnect } = useDisconnect();

  // Demo wallet stays fully in-memory (private key never persisted).
  const [demoAddress, setDemoAddress] = useState(null);
  const [demoPk, setDemoPk] = useState(null);

  const isDemo = !!demoAddress;
  let address = null, kind = null, chainId = null;
  if (isDemo) {
    address = demoAddress; kind = "demo"; chainId = CHAIN_ID;
  } else if (wcConnected) {
    address = wcAddress ? wcAddress.toLowerCase() : null;
    kind = "injected";
    chainId = wcChainId != null ? Number(wcChainId) : null;
  }

  const openWallet = useCallback(async () => {
    // Opening a real wallet supersedes any demo session.
    setDemoAddress(null); setDemoPk(null);
    await open({ view: "Connect" });
  }, [open]);

  const connectDemo = useCallback(() => {
    if (wcConnected) wcDisconnect();
    const w = Wallet.createRandom();
    setDemoPk(w.privateKey);
    setDemoAddress(w.address.toLowerCase());
  }, [wcConnected, wcDisconnect]);

  const resetDemo = useCallback(() => connectDemo(), [connectDemo]);

  const disconnect = useCallback(() => {
    if (isDemo) { setDemoAddress(null); setDemoPk(null); return; }
    wcDisconnect();
  }, [isDemo, wcDisconnect]);

  const openAccount = useCallback(() => open({ view: "Account" }), [open]);

  const doSwitchNetwork = useCallback(async () => {
    if (isDemo) return;
    try { await switchNetwork(robinhoodChain); } catch (e) { console.debug("[Cross wallet] network switch declined/failed:", e); }
  }, [isDemo, switchNetwork]);

  const signAction = useCallback(async (action, dealId = "new") => {
    if (!address) throw new Error("Connect a wallet first.");
    const ts = Math.floor(Date.now() / 1000);
    const msg = buildMessage(action, dealId, ts);
    let signature;
    if (isDemo) {
      if (!demoPk) throw new Error("Demo wallet session ended. Reconnect the demo wallet.");
      signature = await new Wallet(demoPk).signMessage(msg);
    } else {
      if (!walletProvider) throw new Error("Wallet not available. Reconnect and try again.");
      const signer = await new BrowserProvider(walletProvider).getSigner();
      signature = await signer.signMessage(msg);
    }
    return { address, signature, ts };
  }, [address, isDemo, demoPk, walletProvider]);

  const value = useMemo(() => ({
    address, kind, chainId, connected: !!address, busy: false,
    wrongNetwork: kind === "injected" && chainId != null && chainId !== CHAIN_ID,
    openWallet, openAccount, connectDemo, resetDemo, disconnect,
    switchNetwork: doSwitchNetwork, signAction, CHAIN_ID,
    // legacy alias so existing callers keep working
    connectInjected: openWallet,
  }), [address, kind, chainId, openWallet, openAccount, connectDemo, resetDemo, disconnect, doSwitchNetwork, signAction]);

  return <WalletCtx.Provider value={value}>{children}</WalletCtx.Provider>;
}

export const useWallet = () => useContext(WalletCtx);
